/**
 * Profit Vehicle Day Map — Shared types and helpers for the vehicle/order map modal.
 * 
 * Extracted from ProfitabilityReport.tsx to avoid circular dependencies when
 * ProfitVehicleDayMapModal imports these without pulling in the entire report.
 */

import { Warehouse } from 'lucide-react';
import { CHART_PALETTE } from '../config/colors';
import { haversineMetres } from './vehicleDayPlaybackTrack';

// ============================================================================
// Types
// ============================================================================

/** Order shape the map modal needs — subset of ProfitRecord fields */
export interface ProfitMapOrder {
  /**
   * Who collected / delivered the job (zeecore orderLegVehicles): our vehicle,
   * or another depot's number. Absent on older API responses.
   */
  collected_by_vehicle?: string;
  collected_by_depot?: string;
  delivered_by_vehicle?: string;
  delivered_by_depot?: string;
  trunk_vehicle?: string;
  orderId: string;
  vehicleId: string;
  date: string;
  revenue: number;
  cost: number;
  profit: number;
  currency: string;
  status: string;
  journeySplitCount?: number;
  // Route fields
  origin_city?: string;
  destination_city?: string;
  origin_postal_code?: string;
  destination_postal_code?: string;
  origin_end_timestamp?: string;
  origin_end_timestamp_local?: string;
  destination_timestamp?: string;
  destination_end_timestamp_local?: string;
  vehicle_assigned?: string;
  vehicle_pickup?: string;
  vehicle_observed?: string;
  handoff_depot?: string;
  // Geocoded coordinates
  origin_latitude?: number | null;
  origin_longitude?: number | null;
  destination_latitude?: number | null;
  destination_longitude?: number | null;
  destination_coords_source?: 'postcode_centroid';
  // Allocation
  allocation_method?: 'marginal' | 'fallback_equal_split';
  customer?: string;
  goods_quantity?: string | number;
  goods_weight?: string | number;
  order_import_integration_type?: string;
  revenue_imputed?: number;
  service_level_name?: string;
}

export interface TelematicsDeliveryStop {
  AssetName: string;
  day_date: string;
  stop_number: number;
  arrival_time: string;
  departure_time: string;
  duration_minutes: number;
  postcode: string;
  latitude: number;
  longitude: number;
  stop_type: string;
  miles_to_next_stop: number;
}

export interface RouteSegment {
  vehicle: string;
  date: string;
  from_stop_number: number;
  to_stop_number: number;
  segment_start?: string;
  segment_end?: string;
  from_stop?: { lat: number; lon: number; postcode?: string };
  to_stop?: { lat: number; lon: number; postcode?: string };
  n_points_raw?: number;
  n_points_kept?: number;
  points?: Array<[number, number]>;
  coordinates?: Array<[number, number]>;
  label?: string;
  id?: string;
}

export type SyntheticOrderDeliveryStop = {
  stopId: string;
  orderId: string;
  lat: number;
  lon: number;
  postcode?: string;
  city?: string;
};

export type SyntheticOrderHandoffStop = {
  stopId: string;
  orderId: string;
  lat: number;
  lon: number;
  label: string;
  postcode?: string;
};

export type MapSidebarStopRow =
  | { kind: 'telematics'; stop: TelematicsDeliveryStop; stopId: string }
  | { kind: 'order_delivery'; delivery: SyntheticOrderDeliveryStop }
  | { kind: 'order_handoff'; handoff: SyntheticOrderHandoffStop };

// ============================================================================
// Date helpers
// ============================================================================

/**
 * Extract YYYY-MM-DD from any date string (ISO timestamp, DD/MM/YYYY, etc.)
 */
export function getDateOnly(dateStr: string): string {
  if (!dateStr || typeof dateStr !== 'string') return dateStr ?? '';
  return dateStr.length >= 10 ? dateStr.slice(0, 10) : dateStr;
}

/**
 * Normalise telematics date (DD/MM/YYYY or YYYY-MM-DD) to YYYY-MM-DD
 */
export function normalizeTelematicsDate(dateStr: string): string {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const trimmed = dateStr.trim();
  const ddmmyyyy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const [, d, m, y] = ddmmyyyy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return trimmed.length >= 10 ? trimmed.slice(0, 10) : trimmed;
}

// ============================================================================
// Geocoding & map points
// ============================================================================

/**
 * Map pin only when the order has stored origin/destination coordinates.
 * Do not invent a position from telematics stops or route end/start.
 */
export function orderGeocodeMapPoint(
  order: Pick<
    ProfitMapOrder,
    | 'origin_latitude'
    | 'origin_longitude'
    | 'destination_latitude'
    | 'destination_longitude'
  >,
  kind: 'origin' | 'destination',
): { lat: number; lon: number } | null {
  const lat = kind === 'destination' ? order.destination_latitude : order.origin_latitude;
  const lon = kind === 'destination' ? order.destination_longitude : order.origin_longitude;
  if (
    lat != null
    && lon != null
    && Number.isFinite(lat)
    && Number.isFinite(lon)
    && lat !== 0
    && lon !== 0
  ) {
    return { lat, lon };
  }
  return null;
}

// ============================================================================
// Map icon components
// ============================================================================

/**
 * Hub handoff glyph — distinct from both the route blue and delivery amber.
 */
export function HandoffMapIcon({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const glyph =
    size === 'lg' ? 'h-7 w-7' : size === 'sm' ? 'h-3.5 w-3.5' : 'h-6 w-6';
  return (
    <Warehouse
      className={`${glyph} text-chart-10 drop-shadow-lg`}
      fill={CHART_PALETTE[9]}
      stroke="white"
      strokeWidth={size === 'sm' ? 1.5 : 2}
      aria-hidden
    />
  );
}

// ============================================================================
// Stop/route helpers
// ============================================================================

/** Max distance to attach an order delivery to a detected (non Start/End) telematics stop. */
const DELIVERY_STOP_MATCH_MAX_M = 2000;

/**
 * Check if a telematics stop is a route anchor (Start or End).
 * Prefers explicit stop_type; falls back to first/last stop.
 */
export function isTelematicsRouteAnchor(
  stop: TelematicsDeliveryStop,
  stops: TelematicsDeliveryStop[],
): boolean {
  const hasTyped = stops.some((s) => s.stop_type === 'Start' || s.stop_type === 'End');
  if (hasTyped) return stop.stop_type === 'Start' || stop.stop_type === 'End';
  if (stops.length === 0) return false;
  return stop === stops[0] || stop === stops[stops.length - 1];
}

/**
 * Attach deliveries to intermediate telematics stops when close enough.
 * Never tag Start/End — when there is no nearby detected stop, emit a synthetic
 * Delivery row to show between Start and End in the sidebar.
 */
export function buildDeliveryStopSidebarState(
  sortedStops: TelematicsDeliveryStop[],
  destinations: Array<{ orderId: string; lat: number; lon: number; postcode?: string; city?: string }>,
): {
  deliveryStopIds: Set<string>;
  deliveryStopToOrderIds: Map<string, string[]>;
  unmatchedDeliveries: SyntheticOrderDeliveryStop[];
} {
  const deliveryStopIds = new Set<string>();
  const deliveryStopToOrderIds = new Map<string, string[]>();
  const unmatchedDeliveries: SyntheticOrderDeliveryStop[] = [];

  const candidates = sortedStops.filter((s) => !isTelematicsRouteAnchor(s, sortedStops));

  for (const dest of destinations) {
    if (!Number.isFinite(dest.lat) || !Number.isFinite(dest.lon)) continue;
    let bestId: string | null = null;
    let bestM = Infinity;
    for (const stop of candidates) {
      if (stop.latitude == null || stop.longitude == null) continue;
      if (!Number.isFinite(stop.latitude) || !Number.isFinite(stop.longitude)) continue;
      const m = haversineMetres(dest.lat, dest.lon, stop.latitude, stop.longitude);
      if (m < bestM) {
        bestM = m;
        bestId = `stop-${stop.AssetName}-${stop.day_date}-${stop.stop_number}`;
      }
    }
    if (bestId != null && bestM <= DELIVERY_STOP_MATCH_MAX_M) {
      deliveryStopIds.add(bestId);
      const list = deliveryStopToOrderIds.get(bestId) ?? [];
      if (!list.includes(dest.orderId)) list.push(dest.orderId);
      deliveryStopToOrderIds.set(bestId, list);
    } else {
      unmatchedDeliveries.push({
        stopId: `order-delivery-${dest.orderId}`,
        orderId: dest.orderId,
        lat: dest.lat,
        lon: dest.lon,
        postcode: dest.postcode,
        city: dest.city,
      });
    }
  }

  return { deliveryStopIds, deliveryStopToOrderIds, unmatchedDeliveries };
}

/**
 * Telematics Start/End + order-matched stops; GPS-dwell intermediates omitted from STOPS UI.
 */
export function buildMapSidebarStopRows(
  sortedStops: TelematicsDeliveryStop[],
  unmatchedHandoffs: SyntheticOrderHandoffStop[],
  unmatchedDeliveries: SyntheticOrderDeliveryStop[],
  deliveryStopIds: Set<string>,
  proximityOrderMap: Map<string, number> = new Map(),
): MapSidebarStopRow[] {
  const telematicsRows: MapSidebarStopRow[] = sortedStops
    .filter((stop) => {
      if (isTelematicsRouteAnchor(stop, sortedStops)) return true;
      const id = `stop-${stop.AssetName}-${stop.day_date}-${stop.stop_number}`;
      return deliveryStopIds.has(id);
    })
    .map((stop) => ({
      kind: 'telematics' as const,
      stop,
      stopId: `stop-${stop.AssetName}-${stop.day_date}-${stop.stop_number}`,
    }));
  const insertRows: MapSidebarStopRow[] = [
    ...unmatchedHandoffs.map((handoff) => ({ kind: 'order_handoff' as const, handoff })),
    ...unmatchedDeliveries.map((delivery) => ({ kind: 'order_delivery' as const, delivery })),
  ];

  if (insertRows.length === 0) return telematicsRows;

  const orderedInsertRows = [...insertRows]
    .map((row, insertionIndex) => ({ row, insertionIndex }))
    .sort((leftEntry, rightEntry) => {
      const left = leftEntry.row;
      const right = rightEntry.row;
      const getOrderId = (row: MapSidebarStopRow): string | null => {
        if (row.kind === 'order_handoff') return row.handoff.orderId;
        if (row.kind === 'order_delivery') return row.delivery.orderId;
        return null;
      };
      const leftOrderId = getOrderId(left);
      const rightOrderId = getOrderId(right);
      const leftRank = leftOrderId ? proximityOrderMap.get(leftOrderId) : undefined;
      const rightRank = rightOrderId ? proximityOrderMap.get(rightOrderId) : undefined;

      if (leftRank != null || rightRank != null) {
        if (leftRank == null) return 1;
        if (rightRank == null) return -1;
        if (leftRank !== rightRank) return leftRank - rightRank;
      }

      const getCoords = (row: MapSidebarStopRow) => {
        if (row.kind === 'order_handoff') {
          return { lat: row.handoff.lat, lon: row.handoff.lon };
        }
        if (row.kind === 'order_delivery') {
          return { lat: row.delivery.lat, lon: row.delivery.lon };
        }
        return { lat: 0, lon: 0 };
      };

      const leftCoords = getCoords(left);
      const rightCoords = getCoords(right);

      const getNearestStopIndex = (lat: number, lon: number) => {
        let bestIndex = sortedStops.length;
        let bestDistance = Number.POSITIVE_INFINITY;
        for (let idx = 0; idx < sortedStops.length; idx += 1) {
          const stop = sortedStops[idx];
          if (!Number.isFinite(stop.latitude) || !Number.isFinite(stop.longitude)) continue;
          const distance = haversineMetres(lat, lon, stop.latitude, stop.longitude);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = idx;
          }
        }
        return bestIndex;
      };

      const leftPos = getNearestStopIndex(leftCoords.lat, leftCoords.lon);
      const rightPos = getNearestStopIndex(rightCoords.lat, rightCoords.lon);

      if (leftPos !== rightPos) return leftPos - rightPos;
      return leftEntry.insertionIndex - rightEntry.insertionIndex;
    })
    .map(({ row }) => row);

  const hasTypedEnd = sortedStops.some((s) => s.stop_type === 'End');
  let endIdx = hasTypedEnd
    ? telematicsRows.findIndex((r) => r.kind === 'telematics' && r.stop.stop_type === 'End')
    : telematicsRows.length - 1;
  if (endIdx < 0) endIdx = telematicsRows.length;
  return [
    ...telematicsRows.slice(0, endIdx),
    ...orderedInsertRows,
    ...telematicsRows.slice(endIdx),
  ];
}

/**
 * Another depot delivered the job (outbound: we only collected it). Our vehicle's
 * route is then a collection leg, not a delivery leg.
 */
export function orderDeliveredByOtherDepot(
  order: Pick<ProfitMapOrder, 'delivered_by_depot' | 'delivered_by_vehicle'> | null | undefined,
): boolean {
  return Boolean(order?.delivered_by_depot && !order?.delivered_by_vehicle);
}

/** Pickup card / popup: our collecting vehicle, else the collecting depot, else the older field. */
export function orderCollectedByLabel(
  order: Pick<ProfitMapOrder, 'collected_by_vehicle' | 'collected_by_depot' | 'vehicle_pickup'>,
): string {
  if (order.collected_by_vehicle) return order.collected_by_vehicle;
  if (order.collected_by_depot) return `Depot ${order.collected_by_depot}`;
  return order.vehicle_pickup || '';
}
